import { supabase } from "@/integrations/supabase/client";
import { Guard } from "../types";
import { User } from "@supabase/supabase-js";
import { generateCodeFromAFM, generateGuardPassword } from "./utils/guardUtils";
import { retryWithBackoff, delay } from "./utils/retryUtils";
import { assignGuardToTeam } from "./teamService";

/**
 * Creates a new guard or updates an existing one, and optionally assigns to a team
 * @param forceNew - If true, will throw an error if guard already exists instead of updating
 */
export const createGuardWithTeam = async (guardData: any, teamName?: string, forceNew: boolean = false) => {
  const afm = guardData['ΑΦΜ'] || guardData.afm;
  const email = guardData['Email'] || guardData.email;
  const firstName = guardData['Όνομα'] || guardData.first_name;
  const lastName = guardData['Επώνυμο'] || guardData.last_name;
  
  // Ensure we have both first and last name
  if (!firstName || !lastName) {
    throw new Error('Both first name and last name are required');
  }
  
  // Log the data we're processing
  console.log('Processing guard data:', {
    firstName,
    lastName,
    email,
    afm,
    code: guardData['Κωδικός'] || guardData.code
  });
  
  if (afm || email) {
    let query = supabase.from('profiles').select('*').eq('Role', 'guard');
    
    if (afm) {
      query = query.eq('afm', afm);
    } else if (email) {
      query = query.eq('email', email);
    }
    
    const { data: existingProfiles, error: findError } = await query;
    
    if (findError) {
      console.error('Error checking for existing guard:', findError);
    } else if (existingProfiles && existingProfiles.length > 0) {
      const existingGuard = existingProfiles[0];
      const identifierType = afm ? `AFM ${afm}` : `email ${email}`;
      
      // If forceNew is true, throw an error instead of updating
      if (forceNew) {
        throw new Error(`A guard with this ${identifierType} already exists. Please use a different ${afm ? 'AFM' : 'email'}.`);
      }
      
      console.log(`Found existing guard with ${identifierType}. Updating instead of creating.`);
      
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        email: guardData['Email'] || guardData.email,
        hiring_date: guardData['Πρόσληψη'] || guardData.hiring_date || existingGuard.hiring_date,
        date_of_birth: guardData['Ημερ.γέννησης'] || guardData.date_of_birth || existingGuard.date_of_birth,
        license_expiry_date: guardData['ΛΗΞΗ ΑΔΕΙΑΣ'] || guardData.license_expiry_date || existingGuard.license_expiry_date,
        mobile_phone: guardData['Κινητό'] || guardData.mobile_phone || existingGuard.mobile_phone,
        emergency_contact: guardData['Emergency Contact'] || guardData.emergency_contact || existingGuard.emergency_contact,
        code: guardData['Κωδικός'] || guardData.code || existingGuard.code,
        afm: afm || existingGuard.afm,
        full_name: `${firstName} ${lastName}`
      };
      
      console.log('Updating profile with data:', updateData);
      
      const { data: updatedProfile, error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', existingGuard.id)
        .select()
        .single();
      
      if (updateError) {
        console.error('Error updating profile:', updateError);
        throw new Error(`Failed to update profile: ${updateError.message}`);
      }
      
      const result = updatedProfile as unknown as Guard;
      let guardWithWarning: Guard & { teamAssignmentWarning?: string } = result;
      
      if (teamName) {
        try {
          await assignGuardToTeam(existingGuard.id, teamName);
        } catch (teamError: any) {
          console.warn('Team assignment warning:', teamError.message);
          guardWithWarning.teamAssignmentWarning = teamError.message;
        }
      }
      
      return guardWithWarning;
    }
  }
  
  try {
    const code = guardData['Κωδικός'] || guardData.code || generateCodeFromAFM(afm);
    // Use the new auto-generated password pattern
    const autoGeneratedPassword = generateGuardPassword(afm);
    
    console.log(`Using guard code: ${code}`);
    console.log(`Using auto-generated password pattern: G + last 4 AFM digits + #Secure123`);
    
    // Create timeout promise for edge function call (15 seconds)
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Edge function timed out after 15 seconds. Please try again.'));
      }, 15000);
    });
    
    // Edge function call with timeout protection
    const edgeFunctionCall = supabase.functions.invoke('create-guard', {
      body: {
        email: guardData['Email'] || guardData.email,
        password: autoGeneratedPassword,
        userData: {
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`,
          afm: guardData['ΑΦΜ'] || guardData.afm,
          code: code,
          mobile_phone: guardData['Κινητό'] || guardData.mobile_phone || "",
          emergency_contact: guardData['Emergency Contact'] || guardData.emergency_contact || "",
          date_of_birth: guardData['Ημερ.γέννησης'] || guardData.date_of_birth || "",
          hiring_date: guardData['Πρόσληψη'] || guardData.hiring_date || "",
          license_expiry_date: guardData['ΛΗΞΗ ΑΔΕΙΑΣ'] || guardData.license_expiry_date || ""
        }
      }
    });
    
    // Race between edge function and timeout
    let newGuard: any;
    let createError: any;
    
    try {
      const result = await Promise.race([
        edgeFunctionCall,
        timeoutPromise
      ]);
      newGuard = result.data;
      createError = result.error;
    } catch (error) {
      console.log('Edge function failed, using fallback method');
      createError = error;
    }

    // If Edge Function fails, use fallback
    if (createError || !newGuard) {
      console.log('Using fallback: Creating guard with supabase.auth.signUp');
      
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: guardData['Email'] || guardData.email,
        password: autoGeneratedPassword,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create user');

      // Get current user ID for created_by
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      // Update profile with all guard data
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`,
          afm: guardData['ΑΦΜ'] || guardData.afm,
          code: code,
          mobile_phone: guardData['Κινητό'] || guardData.mobile_phone || "",
          emergency_contact: guardData['Emergency Contact'] || guardData.emergency_contact || "",
          date_of_birth: guardData['Ημερ.γέννησης'] || guardData.date_of_birth || "",
          hiring_date: guardData['Πρόσληψη'] || guardData.hiring_date || "",
          license_expiry_date: guardData['ΛΗΞΗ ΑΔΕΙΑΣ'] || guardData.license_expiry_date || "",
          created_by: currentUser?.id
        })
        .eq('id', authData.user.id);

      if (profileError) throw profileError;

      // Assign guard role
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: authData.user.id,
          role: 'guard'
        });

      if (roleError) throw roleError;

      newGuard = { id: authData.user.id, profile: null };
    }

    // Use the profile data returned from the edge function
    const profileData = newGuard.profile || await retryWithBackoff(
      async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', newGuard.id)
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('Profile not found yet');
        
        return data;
      },
      5, // max retries
      1000 // initial delay 1s
    );

    let teamAssignmentWarning = null;
    if (teamName) {
      try {
        await assignGuardToTeam(newGuard.id, teamName);
      } catch (error: any) {
        console.error('Error in team assignment:', error);
        teamAssignmentWarning = `Warning: Guard created but team assignment failed: ${error.message}`;
      }
    }

    const result = profileData as unknown as Guard;
    let guardWithWarning: Guard & { teamAssignmentWarning?: string } = result;
    
    if (teamAssignmentWarning) {
      console.warn(teamAssignmentWarning);
      guardWithWarning.teamAssignmentWarning = teamAssignmentWarning;
    }

    return guardWithWarning;
  } catch (error: any) {
    if (error.message && error.message.includes('already exists') && email) {
      console.log(`User with email ${email} already exists. Attempting to find and update profile.`);
      
      const { data: existingProfiles, error: findError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .eq('Role', 'guard');
        
      if (findError) {
        console.error('Error finding existing profile:', findError);
        throw error;
      }
      
      if (existingProfiles && existingProfiles.length > 0) {
        const existingGuard = existingProfiles[0];
        console.log(`Found existing profile for email ${email}. Updating instead.`);
        
        const updateData = {
          first_name: firstName,
          last_name: lastName,
          hiring_date: guardData['Πρόσληψη'] || guardData.hiring_date || existingGuard.hiring_date,
          date_of_birth: guardData['Ημερ.γέννησης'] || guardData.date_of_birth || existingGuard.date_of_birth,
          license_expiry_date: guardData['ΛΗΞΗ ΑΔΕΙΑΣ'] || guardData.license_expiry_date || existingGuard.license_expiry_date,
          mobile_phone: guardData['Κινητό'] || guardData.mobile_phone || existingGuard.mobile_phone,
          emergency_contact: guardData['Emergency Contact'] || guardData.emergency_contact || existingGuard.emergency_contact,
          code: guardData['Κωδικός'] || guardData.code || existingGuard.code,
          afm: afm || existingGuard.afm,
          full_name: `${firstName} ${lastName}`
        };
        
        const { data: updatedProfile, error: updateError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', existingGuard.id)
          .select()
          .single();
          
        if (updateError) {
          console.error('Error updating existing profile:', updateError);
          throw updateError;
        }
        
        const result = updatedProfile as unknown as Guard;
        let guardWithWarning: Guard & { teamAssignmentWarning?: string } = result;
        
        if (teamName) {
          try {
            await assignGuardToTeam(existingGuard.id, teamName);
          } catch (teamError: any) {
            console.warn('Team assignment warning:', teamError.message);
            guardWithWarning.teamAssignmentWarning = teamError.message;
          }
        }
        
        return guardWithWarning;
      }
    }
    
    throw error;
  }
};
